import { useQuery, useMutation, useQueryClient } from 'react-query';
import { predictionArbitrageManagerAxiosApi } from 'src/Config/axiosApi';
import { PositionsGroupedByPair } from '../types';

interface PositionStats {
  total: number;
  open: number;
  closed: number;
  expired: number;
  totalInvested: number;
  totalUnrealizedPnl: number;
}

export interface PredictionPerformance {
  initialValue: number;
  initialDate: string;
  kalshi: {
    balanceUsd: number;
    portfolioValueUsd: number;
    totalUsd: number;
  };
  polymarket: {
    positionsValueUsd: number;
    cashBalanceUsd: number;
    totalUsd: number;
  };
  cashflows: {
    depositsUsd: number;
    withdrawalsUsd: number;
    netFlowUsd: number;
    byPlatform: {
      kalshi: { deposits: number; withdrawals: number };
      polymarket: { deposits: number; withdrawals: number };
    };
  };
  baselineValueUsd: number;
  currentValueUsd: number;
  pnlUsd: number;
}

interface PositionsFilters {
  status?: string;
  platform?: string;
}

export function usePositionsGrouped(filters?: PositionsFilters) {
  return useQuery(
    ['positions-grouped', filters],
    async () => {
      const { data } = await predictionArbitrageManagerAxiosApi.get<
        PositionsGroupedByPair[]
      >('/positions/grouped', { params: filters });
      const groups = data.filter(g => g._id != null);
      const unmatched = data.filter(g => g._id == null);
      return { groups, unmatched };
    },
    { keepPreviousData: true, refetchInterval: 5000 }
  );
}

export function usePositionStats() {
  return useQuery(['position-stats'], async () => {
    const { data } =
      await predictionArbitrageManagerAxiosApi.get<PositionStats>(
        '/positions/stats'
      );
    return data;
  });
}

export function usePredictionPerformance() {
  return useQuery(['prediction-performance'], async () => {
    const { data } =
      await predictionArbitrageManagerAxiosApi.get<PredictionPerformance>(
        '/positions/performance'
      );
    return data;
  });
}


export function useCreateCashflow() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: {
      platform: string;
      type: string;
      amountUsd: number;
      note?: string;
      date?: string;
    }) => {
      const { data } =
        await predictionArbitrageManagerAxiosApi.post('/positions/cashflows', dto);
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('prediction-performance');
      }
    }
  );
}

export function useSyncPositions() {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const { data } =
        await predictionArbitrageManagerAxiosApi.post('/positions/sync');
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries('positions-grouped');
        qc.invalidateQueries('position-stats');
        qc.invalidateQueries('prediction-performance');
      }
    }
  );
}
